import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button, ButtonWithLoader } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { SelectWithGroups } from '@neo/primitives/select-with-groups'
import type { AxiosError } from 'axios'
import { Box, Braces, Code, Minus, Plus, Sparkles } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useExternalOrgAppsQuery } from 'v2/pages/settings/integrations/queries'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import z from 'zod'
import { NodevarList } from '../../components/nodevar-list'
import { SecretVariablesPopover } from '../../components/secret-variables.popover'
import { useGenerateFunctionSchemaMutation } from '../../queries'
import type { AiAgentNodeformData } from '../../types'
import { ActionConnectionLabel } from '../../utils/action-connection-label'
import { buildActionOption } from '../../utils/integration-action-options'
import { CodeGenerationModal } from './code-generation-modal'

const getActionIcon = (actionType: string) => {
  if (actionType === 'EXTRACT_VARIABLES') return <Braces className="size-5" />
  if (actionType === 'EXECUTE_ACTION') return <Box className="size-5" />
  if (actionType === 'CUSTOM_CODE') return <Code className="size-5" />
  return null
}

const ACTION_TYPE_OPTIONS = [
  { label: 'Extract variables', value: 'EXTRACT_VARIABLES' },
  { label: 'Execute app action', value: 'EXECUTE_ACTION' },
  { label: 'Custom code', value: 'CUSTOM_CODE' },
]

export const ActionsForm = ({
  form,
  arrayIndex,
  nodeVars = [],
}: {
  form: GenericFormType<AiAgentNodeformData>
  arrayIndex: number
  nodeVars?: string[]
}) => {
  const { data: activeApps, isLoading: isLoadingApps } = useExternalOrgAppsQuery(true)
  const { mutate: generateFunctionSchema, isPending: isGeneratingFunctionSchema } =
    useGenerateFunctionSchemaMutation()
  const [isCodeGenModalOpen, setIsCodeGenModalOpen] = useState(false)

  // Create unique identifiers for actions by concatenating action_name with app.id
  // This is necessary because different apps can have actions with the same name
  // Format: `${action_name}_${app.id}`
  const actionOptions = useMemo(
    () =>
      (activeApps || []).map((app) => ({
        id: app.id,
        label: <ActionConnectionLabel app={app} />,
        options:
          app?.actions?.map((appAction) =>
            buildActionOption(appAction, `${appAction.action_name}_${app.id}`),
          ) ?? [],
      })),
    [activeApps],
  )

  // Handle action selection by extracting the original action_name from the concatenated value
  // The concatenated value helps us uniquely identify which app the action belongs to
  const handleActionSelect = useCallback(
    (selectedActionId: string) => {
      // Extract the action name and app ID from the concatenated value
      const underscoreIdx = selectedActionId.lastIndexOf('_')
      if (underscoreIdx === -1) {
        logger.warn(`Unexpected action id format: ${selectedActionId}`)
        return
      }
      const actionName = selectedActionId.slice(0, underscoreIdx)
      const appId = selectedActionId.slice(underscoreIdx + 1)
      // Find the app with the exact matching ID
      const appWithAction = (activeApps || []).find((app) => app.id === appId)
      if (appWithAction) {
        const selectedAction = appWithAction.actions.find(
          (action) => action.action_name === actionName,
        )
        if (selectedAction) {
          form.setFieldValue(`actions[${arrayIndex}].org_app_uuid`, appWithAction.id)
          // Store only the original action_name in the form state.
          form.setFieldValue(`actions[${arrayIndex}].action_name`, selectedAction.action_name)
          form.validateField(`actions[${arrayIndex}].action_name`, 'change')
        }
      }
    },
    [activeApps, form, arrayIndex],
  )

  const fields: GenericFieldConfig<AiAgentNodeformData> = useMemo(
    () => [
      {
        name: 'actions[0].action_type',
        componentType: 'select',
        zodValidation: z.enum(['EXTRACT_VARIABLES', 'EXECUTE_ACTION', 'CUSTOM_CODE'], {
          message: 'Invalid action type',
        }),
        inputProps: {
          options: ACTION_TYPE_OPTIONS,
          searchable: false,
          placeholder: 'Select action type',
          'data-testid': 'select-action-type',
        },
      },
      {
        name: 'actions[0].action_name',
        type: 'custom',
        component: ({ value }) => {
          // Get the current org_app_uuid from the form
          const currentAppId = form.state.values.actions[arrayIndex].org_app_uuid

          // For display purposes, reconstruct the value using the current app ID
          const displayValue = currentAppId ? `${value}_${currentAppId}` : value

          return (
            <SelectWithGroups
              groups={actionOptions}
              loading={isLoadingApps}
              placeholder="Select an app action"
              value={displayValue}
              onChange={(option) => {
                handleActionSelect(option || '')
              }}
              data-testid="select-action"
            />
          )
        },
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'EXECUTE_ACTION'),
        zodValidation: z.string({ invalid_type_error: 'Action name is required' }).min(1, {
          message: 'Action name is required',
        }),
      },
      {
        name: 'actions[0].action_instructions',
        type: 'sub-item',
        componentType: 'textarea',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'EXECUTE_ACTION'),
        zodValidation: z.string({ invalid_type_error: 'Action instructions are required' }).min(1, {
          message: 'Action instructions are required',
        }),
        inputProps: {
          label: 'Action instructions',
          placeholder: 'Provide information and describe when this action should be executed.',
          className: 'min-h-[120px] bg-background',
          'data-testid': 'textarea-action-instructions',
          id: `ai-action-instructions-${arrayIndex}`,
          endIcon: (
            <NodevarList
              nodevars={nodeVars}
              className="pr-2"
              inputElementSelector={`#ai-action-instructions-${arrayIndex}`}
            />
          ),
        },
      },
      {
        name: 'actions[0].variable_name',
        componentType: 'input',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'EXTRACT_VARIABLES'),
        zodValidation: z.string({ invalid_type_error: 'Variable name is required' }).min(1, {
          message: 'Variable name is required',
        }),
        eventDataExtractor: (e) =>
          z
            .string()
            .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
            .parse(e.target?.value),
        inputProps: {
          placeholder: 'Enter variable name to extract',
        },
      },
      {
        name: 'actions[0].variable_instructions',
        type: 'sub-item',
        componentType: 'textarea',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'EXTRACT_VARIABLES'),
        zodValidation: z
          .string({ invalid_type_error: 'Variable instructions are required' })
          .min(1, {
            message: 'Variable instructions are required',
          }),
        inputProps: {
          label: 'Variable instructions',
          placeholder: 'Variable description (e.g. extract the users email from the conversation)',
          className: 'min-h-[120px] bg-background',
          'data-testid': 'textarea-variable-instructions',
        },
      },
      {
        name: 'actions[0].code_name',
        type: 'sub-item',
        componentType: 'input',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'CUSTOM_CODE'),
        zodValidation: z.string({ invalid_type_error: 'Function name is required' }).min(1, {
          message: 'Function name is required',
        }),
        eventDataExtractor: (e) =>
          z
            .string()
            .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
            .parse(e.target?.value),
        inputProps: {
          placeholder: 'Enter function name',
          'data-testid': 'input-function-name',
        },
      },
      {
        name: 'actions[0].code_description',
        type: 'sub-item',
        componentType: 'textarea',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'CUSTOM_CODE'),
        zodValidation: z.string({ invalid_type_error: 'Function description is required' }).min(1, {
          message: 'Function description is required',
        }),
        inputProps: {
          label: 'Function Description',
          placeholder: 'Describe what this function does and when to use it.',
          className: 'min-h-[120px] bg-background',
          'data-testid': 'textarea-function-description',
        },
      },
      {
        name: 'actions[0].code_body',
        type: 'custom',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'CUSTOM_CODE'),
        eventDataExtractor: (data) => data,
        component: ({ onChange, value }) => (
          <div className="-mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-s-600 text-secondary">
                Code <span className="text-s-400 italic text-tertiary">(Python3)</span>
              </span>
              <div className="flex items-center gap-1">
                <ClipboardCopyButton
                  textToCopy={value}
                  tooltipText="Copy code"
                  className="size-4 text-secondary"
                  data-testid="button-copy-code"
                />
                <SecretVariablesPopover
                  onVariableClick={(variable) => {
                    const currentCode = form.getFieldValue(
                      `actions[${arrayIndex}].code_body`,
                    ) as string
                    let newCode = currentCode

                    // First, check if we need to add import os
                    const hasImportOs = currentCode.includes('import os')
                    const envVar = `${variable.name} = os.environ.get("${variable.name}")`
                    const hasEnvVar = currentCode.includes(envVar)

                    if (!hasImportOs && !hasEnvVar) {
                      // Add both import and variable
                      newCode = `import os\n${envVar}\n\n${currentCode}`
                    } else if (!hasImportOs) {
                      // Add just the import at the top
                      newCode = `import os\n${currentCode}`
                    } else if (!hasEnvVar) {
                      // Find the position after import os
                      const lines = currentCode.split('\n')
                      const importOsIndex = lines.findIndex((line) => line.includes('import os'))

                      // Insert the env var after the imports
                      lines.splice(importOsIndex + 1, 0, envVar)
                      newCode = lines.join('\n')
                    }

                    form.setFieldValue(`actions[${arrayIndex}].code_body`, newCode)
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => setIsCodeGenModalOpen(true)}
                  data-testid="button-open-code-gen"
                  aria-label="Generate code"
                  className="form-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate code
                </Button>
              </div>
            </div>
            <CodeEditor
              language="python"
              value={value}
              onChange={onChange}
              className="border border-st2 rounded-md overflow-auto"
              data-testid="code-editor"
            />
          </div>
        ),
        zodValidation: z.string({ invalid_type_error: 'Code body is required' }).min(1, {
          message: 'Code body is required',
        }),
      },
      {
        type: 'slot',
        name: 'gen-schema-button',
        element: ({ form: f }) =>
          f.state.values.actions[arrayIndex].action_type === 'CUSTOM_CODE' && (
            <div className="mt-2">
              <ButtonWithLoader
                loading={isGeneratingFunctionSchema}
                variant="outline"
                type="button"
                size="sm"
                onClick={() => {
                  const currentValue = f.state.values.actions[arrayIndex]
                  generateFunctionSchema(
                    {
                      code: currentValue.code_body || '',
                      function_name: currentValue.code_name || '',
                      function_description: currentValue.code_description || '',
                    },
                    {
                      onSuccess: (data) => {
                        f.setFieldValue(
                          `actions[${arrayIndex}].code_function_schema`,
                          data.function_schema,
                        )
                      },
                      onError: (error) => {
                        errorToast({
                          title: 'Failed to generate function schema',
                          description: getErrorText(error as AxiosError),
                        })
                      },
                    },
                  )
                }}
              >
                Generate Function Schema
              </ButtonWithLoader>
            </div>
          ),
      },
      {
        name: 'actions[0].code_function_schema',
        type: 'custom',
        visible: (values, index) =>
          Boolean(values.actions?.[index ?? 0]?.action_type === 'CUSTOM_CODE'),
        component: ({ onChange, value }) => {
          if (!value) return null
          const jsonValue = JSON.stringify(value, null, 2)

          return (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-s-600 text-secondary">
                  Function Schema <span className="text-s-400 italic text-tertiary">(JSON)</span>
                </span>
                <ClipboardCopyButton
                  textToCopy={jsonValue}
                  tooltipText="Copy function schema"
                  className="mr-1 size-4 text-secondary"
                  data-testid="button-copy-schema"
                />
              </div>
              <CodeEditor
                language="json"
                value={JSON.stringify(value, null, 2)}
                className="border border-st2 rounded-md overflow-auto bg-background"
                data-testid="code-editor-schema"
                onChange={(code) => {
                  try {
                    onChange(JSON.parse(code))
                  } catch (_e) {
                    // Handle invalid JSON silently
                  }
                }}
              />
            </div>
          )
        },
        eventDataExtractor: (data) => data,
        zodValidation: z.object(
          {},
          {
            invalid_type_error: 'Function schema is required. Click the button to generate.',
          },
        ),
      },
    ],
    [
      arrayIndex,
      actionOptions,
      isGeneratingFunctionSchema,
      generateFunctionSchema,
      isLoadingApps,
      handleActionSelect,
      form,
      setIsCodeGenModalOpen,
      nodeVars,
    ],
  )

  // const orgAppUuid = form.state.values.actions[arrayIndex].org_app_uuid

  // const appIcon = useMemo(() => {
  //   const app = activeApps?.find((a) => a.id === orgAppUuid)
  //   return app?.logo_url
  // }, [activeApps, orgAppUuid])

  return (
    <div className="w-full max-w-2xl mx-auto mt-4">
      <div className="relative rounded-lg border p-4 flex flex-col gap-4 has-[.field-error-message]:border-destructive">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between relative w-full pr-12">
            <form.Field
              name={`actions[${arrayIndex}].name`}
              validators={{
                onChange: z.string().optional().nullable(),
              }}
              children={(field) => (
                <div className="w-full">
                  <div className="flex items-center gap-2">
                    {getActionIcon(form.state.values.actions[arrayIndex].action_type || '')}
                    <div className="flex items-center gap-2 w-full">
                      <Input
                        className="text-p-500 text-secondary w-full p-0 h-5 shadow-none"
                        inputClassName={cn(
                          'border-0 focus:border-b focus:border-solid focus:border-primary truncate focus:-top-1 relative transition-top duration-700',
                          !field.state.value && 'focus:border-destructive',
                        )}
                        value={field.state.value ?? ''}
                        variant="ghost"
                        autoFocus
                        onChange={(e) => {
                          field.handleChange(e.target.value)
                        }}
                      />
                    </div>
                  </div>
                  {!form.state.values.actions[arrayIndex].is_expanded && (
                    <div className="flex flex-col gap-3">
                      <div className="text-xs-400 text-tertiary pl-7 truncate">
                        {form.state.values.actions[arrayIndex].code_description ||
                          form.state.values.actions[arrayIndex].variable_instructions ||
                          form.state.values.actions[arrayIndex].action_instructions}
                      </div>
                    </div>
                  )}
                </div>
              )}
            />
            <form.Field
              name={`actions[${arrayIndex}].is_expanded`}
              children={(field) => (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  className="p-0 hover:bg-transparent absolute right-0 -top-2"
                  onClick={() => field.handleChange(!field.state.value)}
                  data-testid="button-toggle-action"
                  aria-expanded={field.state.value}
                  aria-label={field.state.value ? 'Collapse action' : 'Expand action'}
                >
                  {field.state.value ? (
                    <Minus className="h-4 w-4 text-secondary" />
                  ) : (
                    <Plus className="h-4 w-4 text-secondary" />
                  )}
                </Button>
              )}
            />
          </div>
          {!form.state.values.actions[arrayIndex].is_expanded &&
            !form.state.values.actions[arrayIndex].action_type && (
              <form.Field
                name={`actions[${arrayIndex}].action_type`}
                children={(typeField) => (
                  <Select
                    value={typeField.state.value || ''}
                    onChange={(value) => {
                      typeField.handleChange(
                        value as 'EXTRACT_VARIABLES' | 'EXECUTE_ACTION' | 'CUSTOM_CODE',
                      )
                      form.setFieldValue(`actions[${arrayIndex}].is_expanded`, true)
                    }}
                    options={ACTION_TYPE_OPTIONS}
                    searchable={false}
                    placeholder="Select type"
                    data-testid="select-action-type"
                  />
                )}
              />
            )}
        </div>
        <div
          className={cn(
            'flex flex-col gap-4',
            !form.state.values.actions[arrayIndex].is_expanded && 'hidden',
          )}
        >
          <FieldsRenderer
            form={form}
            fields={fields}
            baseName={`actions[${arrayIndex}]`}
            arrayIndex={arrayIndex}
          />
        </div>
      </div>

      <CodeGenerationModal
        isOpen={isCodeGenModalOpen}
        onClose={() => setIsCodeGenModalOpen(false)}
        form={form}
        arrayIndex={arrayIndex}
        onCodeGenerated={(code, schema, name, description) => {
          form.setFieldValue(`actions[${arrayIndex}].code_body`, code)
          form.setFieldValue(`actions[${arrayIndex}].code_name`, name)
          form.setFieldValue(`actions[${arrayIndex}].code_function_schema`, schema)
          form.setFieldValue(`actions[${arrayIndex}].code_description`, description)
          form.validateAllFields('change')
        }}
      />
    </div>
  )
}
